import axios from "axios";
import Cookies from "js-cookie";
import { API_URL } from "helper/Constants";

class DepositService {
  constructor(baseURL) {
       this.api = axios.create({
      baseURL: baseURL ? baseURL : API_URL,
    });

    this.api.interceptors.request.use(
      async (config) => {
        const token = Cookies.get("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response && (error.response.status === 401 && error.response.data.message !== "Invalid refresh token")) {
          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            window?.location?.reload()
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async refreshToken() {
    try {
      const response = await this.api.post(
        "/auth/refresh-token",
        {},
        {
          withCredentials: true,
        }
      );
      const newAccessToken = response.data.accessToken;
      Cookies.set("token", newAccessToken, {
        secure: true,
        sameSite: "None",
      });
      return newAccessToken;
    } catch (error) {
      Cookies.remove("user", { secure: true, sameSite: "None" });
      Cookies.remove("token", { secure: true, sameSite: "None" });
      Cookies.remove("refreshToken", { secure: true, sameSite: "None" });
      window?.location?.reload()
      console.error("Failed to refresh token:", error);
      return null;
    }
  }

  async fetchDeposits({ 
    status, 
    user, 
    paymentMethod, 
    startDate, 
    endDate, 
    page_number = 1, 
    items_per_page = 50 
  } = {}) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    try {
      const params = {
        page_number,
        items_per_page,
      };

      if (status) params.status = status;
      if (user) params.user = user;
      if (paymentMethod) params.paymentMethod = paymentMethod;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await this.api.get(`/api/deposits`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch deposits.");
      }
    } catch (error) {
      console.error("Error fetching deposits:", error);
      throw error;
    }
  }

  async fetchUserDeposits({ 
    status, 
    page_number = 1, 
    items_per_page = 50 
  } = {}) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    try {
      const params = {
        page_number,
        items_per_page,
      };

      if (status) params.status = status;

      const response = await this.api.get(`/api/deposits/user/my-deposits`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch user deposits.");
      }
    } catch (error) {
      console.error("Error fetching user deposits:", error);
      throw error;
    }
  }

  async fetchDepositById(id) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    if (!id) {
      throw new Error("Deposit ID is required");
    }

    try {
      const response = await this.api.get(`/api/deposits/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch deposit.");
      }
    } catch (error) {
      console.error("Error fetching deposit:", error);
      throw error;
    }
  }


async createDeposit(depositData, screenshotFile = null) {
  const token = Cookies.get("token");

  if (!token) {
    throw new Error("User is not authenticated. Token is missing.");
  }

  const { paymentMethod, amount, notes, username } = depositData;

  if (!paymentMethod || !amount || !username) {
    throw new Error("Username, payment method, and amount are required");
  }

  try {
    const formData = new FormData();
    formData.append('paymentMethod', paymentMethod);
    formData.append('amount', amount);
    formData.append('username', username); // new addition

    if (notes) {
      formData.append('notes', notes);
    }

    if (screenshotFile) {
      formData.append('screenshot', screenshotFile);
    }

    const response = await this.api.post(`/api/deposits`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Failed to create deposit.");
    }
  } catch (error) {
    console.error("Error creating deposit:", error);
    throw error;
  }
}


  async cancelDeposit(id) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    if (!id) {
      throw new Error("Deposit ID is required");
    }

    try {
      const response = await this.api.put(
        `/api/deposits/${id}/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || "Failed to cancel deposit.");
      }
    } catch (error) {
      console.error("Error cancelling deposit:", error);
      throw error;
    }
  }

  async approveDeposit(id, adminNotes = '') {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    if (!id) {
      throw new Error("Deposit ID is required");
    }

    try {
      const response = await this.api.patch(
        `/api/deposits/${id}/approve`,
        { adminNotes },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || "Failed to approve deposit.");
      }
    } catch (error) {
      console.error("Error approving deposit:", error);
      throw error;
    }
  }

  async rejectDeposit(id, rejectionReason, adminNotes = '') {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    if (!id) {
      throw new Error("Deposit ID is required");
    }

    if (!rejectionReason) {
      throw new Error("Rejection reason is required");
    }

    try {
      const response = await this.api.patch(
        `/api/deposits/${id}/reject`,
        { rejectionReason, adminNotes },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || "Failed to reject deposit.");
      }
    } catch (error) {
      console.error("Error rejecting deposit:", error);
      throw error;
    }
  }

  async fetchPendingDeposits() {
    return await this.fetchDeposits({ status: 'pending' });
  }

  async fetchApprovedDeposits() {
    return await this.fetchDeposits({ status: 'approved' });
  }

  async fetchRejectedDeposits() {
    return await this.fetchDeposits({ status: 'rejected' });
  }

  async fetchUserPendingDeposits() {
    return await this.fetchUserDeposits({ status: 'pending' });
  }

  async fetchUserApprovedDeposits() {
    return await this.fetchUserDeposits({ status: 'approved' });
  }

  async fetchUserRejectedDeposits() {
    return await this.fetchUserDeposits({ status: 'rejected' });
  }

  async hasPendingDeposit() {
    try {
      const response = await this.fetchUserPendingDeposits();
      return response.data && response.data.length > 0;
    } catch (error) {
      console.error("Error checking pending deposit:", error);
      return false;
    }
  }
}

export default DepositService;